import type { ReactNode } from 'react'
import type { RsuiteColor } from '@@partner:types:app'

import React from 'react'
import { Checkbox } from 'rsuite'
import FilterField, { FilterFieldSize } from './FilterField'
import FilterFields from './FilterFields'
import FilterSection from './FilterSection'
import FilterExpandable from './FilterExpandable'
import FilterInput from './FilterInput'
import FilterMultilineInput from './FilterMultilineInput'
import FilterNumberRange from './FilterNumberRange'
import { FilterDateRange, FilterDate } from './FilterDateRange'
import { FilterSelect, FilterCheckPicker } from './FilterSelect'

// Тип для данных SelectPicker/CheckPicker
export interface FilterDataItem {
  label: string
  value: string | number
  color?: RsuiteColor
  [key: string]: any
}

// ===== ТИПЫ ФИЛЬТРОВ =====

export type FilterType =
  | 'text'           // Простой текстовый инпут
  | 'multiline'      // Textarea для списков
  | 'select'         // SelectPicker
  | 'multiselect'    // CheckPicker
  | 'checkbox'       // Чекбокс
  | 'number-range'   // От - До (числа)
  | 'date'           // Одна дата
  | 'date-range'     // Период дат
  | 'custom'         // Кастомный компонент

// ===== БАЗОВЫЙ КОНФИГ ФИЛЬТРА =====

export interface FilterConfigBase {

  /** Уникальный ключ фильтра (соответствует полю в state) */

  key: string

  /** Плейсхолдер */

  placeholder?: string

  /** Размер поля */

  size?: FilterFieldSize

  /** Скрыть на основном экране (показывать только в раскрывашке) */

  expandable?: boolean

  /** Секция для группировки (только для expandable) */

  section?: string
}

// ===== КОНФИГИ ПО ТИПАМ =====

export interface TextFilterConfig extends FilterConfigBase {
  type: 'text'
}

export interface MultilineFilterConfig extends FilterConfigBase {
  type: 'multiline'
  rows?: number

  /** Regex для валидации каждой строки */

  linePattern?: RegExp
}

export interface SelectFilterConfig extends FilterConfigBase {
  type: 'select'
  data: FilterDataItem[]
  searchable?: boolean
}

export interface MultiselectFilterConfig extends FilterConfigBase {
  type: 'multiselect'
  data: FilterDataItem[]
  searchable?: boolean
  renderMenuItem?: (label: ReactNode, item: FilterDataItem) => ReactNode
}

export interface NumberRangeFilterConfig extends FilterConfigBase {
  type: 'number-range'
  placeholderFrom?: string
  placeholderTo?: string
  min?: number
  max?: number
  step?: number
}

export interface DateFilterConfig extends FilterConfigBase {
  type: 'date'
}

export interface DateRangeFilterConfig extends FilterConfigBase {
  type: 'date-range'
}

export interface CheckboxFilterConfig extends FilterConfigBase {
  type: 'checkbox'
  label?: string
}

export interface CustomFilterRenderProps {
  value: any
  onChange: (value: any) => void
}

/** Тип значения для парсинга URL параметров */
export type FilterValueType = 
  | 'string'        // Строка (по умолчанию для text)
  | 'number'        // Число
  | 'boolean'       // Boolean (по умолчанию для checkbox)
  | 'string[]'      // Массив строк (по умолчанию для multiselect)
  | 'number[]'      // Массив чисел
  | 'date'          // Timestamp (по умолчанию для date)
  | 'date-range'    // Объект { from, to } (по умолчанию для date-range)

export interface CustomFilterConfig extends FilterConfigBase {
  type: 'custom'

  /** Функция рендера кастомного компонента */
  render: (props: CustomFilterRenderProps) => ReactNode
  
  /** Тип значения для парсинга URL параметров */
  valueType?: FilterValueType
}

export type FilterConfig = 
  | TextFilterConfig
  | MultilineFilterConfig
  | SelectFilterConfig
  | MultiselectFilterConfig
  | NumberRangeFilterConfig
  | DateFilterConfig
  | DateRangeFilterConfig
  | CheckboxFilterConfig
  | CustomFilterConfig

// ===== ХЕЛПЕРЫ ДЛЯ РАБОТЫ С КОНФИГОМ =====

export interface FilterState {
  [key: string]: any
}

export interface FilterRenderProps<T extends FilterState = FilterState> {
  filters: FilterConfig[]
  state: T
  onChange: <K extends keyof T>(key: K, value: T[K]) => void

  /** Для multiline фильтров - сырые значения */

  rawValues?: Record<string, string>
  onRawChange?: (key: string, value: string) => void
  onRawBlur?: (key: string) => void
}

// ===== РЕНДЕР ОДНОГО ФИЛЬТРА =====

function renderFilterComponent<T extends FilterState> (
  config: FilterConfig,
  state: T,
  onChange: <K extends keyof T>(key: K, value: T[K]) => void,
  rawValues?: Record<string, string>,
  onRawChange?: (key: string, value: string) => void,
  onRawBlur?: (key: string) => void
): ReactNode {
  const { key, placeholder } = config

  switch (config.type) {
  case 'text':
    return (
      <FilterInput
        value={state[key] || ''}
        onChange={v => onChange(key as keyof T, v as T[keyof T])}
        placeholder={placeholder}
      />
    )

  case 'multiline':
    return (
      <FilterMultilineInput
        value={rawValues?.[key] ?? ''}
        onChange={v => onRawChange?.(key, v)}
        onBlur={() => onRawBlur?.(key)}
        placeholder={placeholder}
        rows={config.rows}
      />
    )

  case 'select':
    return (
      <FilterSelect
        value={state[key]}
        data={config.data}
        onChange={v => onChange(key as keyof T, v as T[keyof T])}
        placeholder={placeholder}
        searchable={config.searchable}
      />
    )

  case 'multiselect':
    return (
      <FilterCheckPicker
        value={state[key]}
        data={config.data}
        onChange={v => onChange(key as keyof T, (v || []) as T[keyof T])}
        placeholder={placeholder}
        searchable={config.searchable}
        renderMenuItem={config.renderMenuItem}
      />
    )

  case 'number-range': {
    // Для number-range ожидаем что в state есть поля keyFrom и keyTo
    const fromKey = `${key}From` as keyof T
    const toKey = `${key}To` as keyof T
    return (
      <FilterNumberRange
        from={state[fromKey]}
        to={state[toKey]}
        onFromChange={v => onChange(fromKey, v as T[keyof T])}
        onToChange={v => onChange(toKey, v as T[keyof T])}
        placeholderFrom={config.placeholderFrom ?? `${placeholder} от`}
        placeholderTo={config.placeholderTo ?? `${placeholder} до`}
        min={config.min}
        max={config.max}
        step={config.step}
      />
    )
  }

  case 'date': {
    const value = state[key]
    return (
      <FilterDate
        value={value ? new Date(value) : undefined}
        onChange={v => onChange(key as keyof T, (v ? v.getTime() : undefined) as T[keyof T])}
        placeholder={placeholder}
      />
    )
  }

  case 'date-range': {
    // Для date-range ожидаем объект { from, to }
    const rangeValue = state[key] as { from?: number; to?: number } | undefined
    // Передаём объект даже если только одно значение - FilterDateRange покажет placeholder
    return (
      <FilterDateRange
        value={rangeValue}
        onChange={v => onChange(key as keyof T, v as T[keyof T])}
        placeholder={placeholder}
      />
    )
  }

  case 'checkbox':
    return (
      <Checkbox
        checked={!!state[key]}
        onChange={(_value, checked) => onChange(key as keyof T, checked as T[keyof T])}
      >
        {config.label ?? placeholder}
      </Checkbox>
    )

  case 'custom':
    return config.render({
      value: state[key],
      onChange: v => onChange(key as keyof T, v as T[keyof T])
    })

  default:
    return null
  }
}

// ===== КОМПОНЕНТ РЕНДЕРА ФИЛЬТРОВ =====

export interface FilterRendererProps<T extends FilterState> extends FilterRenderProps<T> {
  submitButton?: ReactNode
  bottomControls?: ReactNode
}

export function FilterRenderer<T extends FilterState> ({
  filters,
  state,
  onChange,
  rawValues,
  onRawChange,
  onRawBlur,
  submitButton,
  bottomControls
}: FilterRendererProps<T>): JSX.Element {
  // Разделяем фильтры на основные и expandable
  const mainFilters = filters.filter(f => !f.expandable)
  const expandableFilters = filters.filter(f => f.expandable)

  // Группируем expandable по секциям
  const sections = expandableFilters.reduce((acc, filter) => {
    const section = filter.section || 'Дополнительно'
    if (!acc[section]) {
      acc[section] = []
    }
    acc[section].push(filter)
    return acc
  }, {} as Record<string, FilterConfig[]>)

  return (
    <>
      {/* Основные фильтры + кнопка поиска */}
      <div className="FilterPanel__MainRow">
        {mainFilters.length > 0 && (
          <FilterFields>
            {mainFilters.map(config => (
              <FilterField key={config.key} size={config.size}>
                {renderFilterComponent(config, state, onChange, rawValues, onRawChange, onRawBlur)}
              </FilterField>
            ))}
          </FilterFields>
        )}
        
        {submitButton && (
          <div className="FilterPanel__SubmitButton">
            {submitButton}
          </div>
        )}
      </div>

      {/* Дополнительные фильтры */}
      {expandableFilters.length > 0 && (
        <FilterExpandable label="Дополнительные фильтры">
          {Object.entries(sections).map(([ sectionTitle, sectionFilters ]) => (
            <FilterSection key={sectionTitle} title={sectionTitle}>
              {sectionFilters.map(config => (
                <FilterField key={config.key} size={config.size}>
                  {renderFilterComponent(config, state, onChange, rawValues, onRawChange, onRawBlur)}
                </FilterField>
              ))}
            </FilterSection>
          ))}
        </FilterExpandable>
      )}

      {/* Дополнительные контролы (массовое обновление и пр.) */}
      {bottomControls}
    </>
  )
}

// ===== ХЕЛПЕР ДЛЯ СОЗДАНИЯ КОНФИГОВ =====

/** Общие опции для всех типов фильтров */
interface CommonFilterOptions {
  size?: FilterFieldSize
  expandable?: boolean
  section?: string
}

export const createFilter = {
  text: (
    key: string, 
    placeholder?: string, 
    options?: CommonFilterOptions
  ): TextFilterConfig => ({
    type: 'text',
    key,
    placeholder,
    ...options
  }),

  multiline: (
    key: string,
    placeholder?: string,
    options?: { rows?: number; linePattern?: RegExp } & CommonFilterOptions
  ): MultilineFilterConfig => ({
    type: 'multiline',
    key,
    placeholder,
    ...options
  }),

  select: (
    key: string,
    data: FilterDataItem[],
    placeholder?: string,
    options?: { searchable?: boolean } & CommonFilterOptions
  ): SelectFilterConfig => ({
    type: 'select',
    key,
    data,
    placeholder,
    ...options
  }),

  multiselect: (
    key: string,
    data: FilterDataItem[],
    placeholder?: string,
    options?: { 
      searchable?: boolean
      renderMenuItem?: (label: ReactNode, item: FilterDataItem) => ReactNode
    } & CommonFilterOptions
  ): MultiselectFilterConfig => ({
    type: 'multiselect',
    key,
    data,
    placeholder,
    ...options
  }),

  numberRange: (
    key: string,
    placeholder?: string,
    options?: { 
      placeholderFrom?: string
      placeholderTo?: string
      min?: number
      max?: number
      step?: number
    } & CommonFilterOptions
  ): NumberRangeFilterConfig => ({
    type: 'number-range',
    key,
    placeholder,
    ...options
  }),

  date: (
    key: string, 
    placeholder?: string, 
    options?: CommonFilterOptions
  ): DateFilterConfig => ({
    type: 'date',
    key,
    placeholder,
    ...options
  }),

  dateRange: (
    key: string, 
    placeholder?: string, 
    options?: CommonFilterOptions
  ): DateRangeFilterConfig => ({
    type: 'date-range',
    key,
    placeholder,
    ...options
  }),

  checkbox: (
    key: string,
    label?: string,
    options?: CommonFilterOptions
  ): CheckboxFilterConfig => ({
    type: 'checkbox',
    key,
    label,
    ...options
  }),

  custom: (
    key: string,
    placeholder: string | undefined,
    render: (props: CustomFilterRenderProps) => ReactNode,
    options?: { valueType?: FilterValueType } & CommonFilterOptions
  ): CustomFilterConfig => ({
    type: 'custom',
    key,
    placeholder,
    render,
    ...options
  })
}

// ===== ПАРСИНГ URL ПАРАМЕТРОВ НА ОСНОВЕ КОНФИГУРАЦИИ =====

/**
 * Парсит URL параметры на основе конфигурации фильтров.
 * Автоматически определяет типы по типу фильтра.
 */
export function parseUrlParamsFromConfig<T extends FilterState>(
  params: Record<string, any>,
  filters: FilterConfig[],
  defaultState: T
): Partial<T> {
  const result: Partial<T> = {}

  for (const filter of filters) {
    const { key } = filter
    const value = params[key]
    
    if (value === undefined) continue

    // Определяем тип значения
    let valueType: FilterValueType | undefined
    
    switch (filter.type) {
    case 'text':
    case 'multiline':
      valueType = 'string'
      break
    case 'select':
      // Определяем по типу значений в data
      valueType = typeof filter.data[0]?.value === 'number' ? 'number' : 'string'
      break
    case 'multiselect':
      valueType = typeof filter.data[0]?.value === 'number' ? 'number[]' : 'string[]'
      break
    case 'checkbox':
      valueType = 'boolean'
      break
    case 'date':
      valueType = 'date'
      break
    case 'date-range':
      valueType = 'date-range'
      break
    case 'number-range':
      // Обрабатывается отдельно ниже
      break
    case 'custom':
      valueType = filter.valueType
      // Если valueType не указан, пытаемся определить по defaultState
      if (!valueType) {
        const defaultValue = defaultState[key]
        if (Array.isArray(defaultValue)) {
          valueType = typeof defaultValue[0] === 'number' ? 'number[]' : 'string[]'
        } else if (typeof defaultValue === 'boolean') {
          valueType = 'boolean'
        } else if (typeof defaultValue === 'number') {
          valueType = 'number'
        }
      }
      break
    }

    // Парсим значение
    switch (valueType) {
    case 'string':
      result[key as keyof T] = String(value) as T[keyof T]
      break
    case 'number': {
      const num = Number(value)
      if (!isNaN(num)) {
        result[key as keyof T] = num as T[keyof T]
      }
      break
    }
    case 'boolean':
      result[key as keyof T] = (value === 'true' || value === true) as T[keyof T]
      break
    case 'string[]': {
      const arr = Array.isArray(value) ? value : [ value ]
      result[key as keyof T] = arr.map(String) as T[keyof T]
      break
    }
    case 'number[]': {
      const arr = Array.isArray(value) ? value : [ value ]
      result[key as keyof T] = arr.map(Number).filter((n: number) => !isNaN(n)) as T[keyof T]
      break
    }
    case 'date': {
      const num = Number(value)
      if (!isNaN(num)) {
        result[key as keyof T] = num as T[keyof T]
      }
      break
    }
    case 'date-range': {
      // Ожидаем объект { from, to } или отдельные параметры
      if (typeof value === 'object' && value !== null) {
        const from = value.from ? Number(value.from) : undefined
        const to = value.to ? Number(value.to) : undefined
        if (from || to) {
          result[key as keyof T] = { from, to } as T[keyof T]
        }
      }
      break
    }
    }

    // Обработка number-range
    if (filter.type === 'number-range') {
      const fromKey = `${key}From`
      const toKey = `${key}To`
      if (params[fromKey] !== undefined) {
        const num = Number(params[fromKey])
        if (!isNaN(num)) {
          result[fromKey as keyof T] = num as T[keyof T]
        }
      }
      if (params[toKey] !== undefined) {
        const num = Number(params[toKey])
        if (!isNaN(num)) {
          result[toKey as keyof T] = num as T[keyof T]
        }
      }
    }
  }

  return result
}
